// /commissions - YTD T4A-eligible total + monthly statement list.
// Phase 6.1 Chunk g.

import { useEffect, useState } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import {
  Card,
  CardLabel,
  CardValue,
  CardDescription,
  Badge,
  statusToBadgeVariant,
  TableWrapper,
  tableStyle,
  theadStyle,
  thStyle,
  trStyle,
  tdStyle,
  colors,
  headingStyle,
  pageWrapperStyle,
  mutedStyle,
  footerStyle,
  errorBannerStyle,
} from '../components/ui';
import { useLang, fmt } from '../i18n';

interface PayoutRow {
  id: string;
  period_start: string;
  period_end: string;
  gross_amount_cad: number;
  adjustments_amount_cad: number | null;
  net_amount_cad: number;
  attribution_count: number;
  status: string;
  paid_at: string | null;
  t4a_eligible: boolean;
  notes: string | null;
}

interface ApiResponse {
  ytd_t4a_eligible_cad: number;
  statements: PayoutRow[];
  count: number;
  note: string | null;
}

export default function Commissions() {
  const { t } = useLang();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/partner/commissions');
        if (!res.ok) {
          if (!cancelled) {
            setError(`HTTP ${res.status}`);
            setLoading(false);
          }
          return;
        }
        const json = (await res.json()) as ApiResponse;
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('network_error');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PortalLayout>
      <div style={{ ...pageWrapperStyle, maxWidth: '1100px' }}>
        <h1 style={headingStyle}>{t.commissions.title}</h1>
        <p style={{ ...mutedStyle, marginBottom: '24px' }}>{t.commissions.subtitle}</p>

        {loading && <div style={mutedStyle}>{t.common.loading}</div>}

        {error && (
          <div style={errorBannerStyle}>
            {fmt(t.commissions.errorMsg, { error })}
          </div>
        )}

        {data && (
          <>
            <Card
              size="lg"
              style={{
                background: `linear-gradient(135deg, ${colors.cyanBg} 0%, rgba(14, 165, 233, 0.05) 100%)`,
                border: `1px solid ${colors.cyanBorder}`,
              }}
            >
              <CardLabel>{t.commissions.ytdTitle}</CardLabel>
              <CardValue style={{ fontSize: '2.5rem', marginTop: '8px' }}>
                ${data.ytd_t4a_eligible_cad.toFixed(2)}
              </CardValue>
              <CardDescription>
                {fmt(t.commissions.ytdDesc, { year: String(new Date().getUTCFullYear()) })}
              </CardDescription>
            </Card>

            {data.note && (
              <Card>
                <p style={mutedStyle}>{data.note}</p>
              </Card>
            )}

            {data.statements.length > 0 && (
              <TableWrapper>
                <table style={tableStyle}>
                  <thead>
                    <tr style={theadStyle}>
                      <th style={thStyle}>{t.commissions.colPeriod}</th>
                      <th style={thStyle}>{t.commissions.colAttributions}</th>
                      <th style={thStyle}>{t.commissions.colGross}</th>
                      <th style={thStyle}>{t.commissions.colAdjust}</th>
                      <th style={thStyle}>{t.commissions.colNet}</th>
                      <th style={thStyle}>{t.commissions.colStatus}</th>
                      <th style={thStyle}>{t.commissions.colPaid}</th>
                      <th style={thStyle}>{t.commissions.colT4a}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.statements.map((s) => (
                      <tr key={s.id} style={trStyle}>
                        <td style={tdStyle}>
                          {fmt(t.commissions.periodRange, { start: s.period_start, end: s.period_end })}
                        </td>
                        <td style={tdStyle}>{s.attribution_count}</td>
                        <td style={tdStyle}>${Number(s.gross_amount_cad).toFixed(2)}</td>
                        <td style={tdStyle}>
                          {s.adjustments_amount_cad
                            ? `$${Number(s.adjustments_amount_cad).toFixed(2)}`
                            : '$0.00'}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          ${Number(s.net_amount_cad).toFixed(2)}
                        </td>
                        <td style={tdStyle}>
                          <Badge variant={statusToBadgeVariant(s.status)}>
                            {s.status}
                          </Badge>
                        </td>
                        <td style={tdStyle}>
                          {s.paid_at ? new Date(s.paid_at).toLocaleDateString() : t.commissions.noPaid}
                        </td>
                        <td style={tdStyle}>
                          {s.t4a_eligible ? '✓' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrapper>
            )}
          </>
        )}

        <div style={footerStyle}>{t.common.poweredBy}</div>
      </div>
    </PortalLayout>
  );
}
